// THE CONVERSATION HELD ABOUT ONE OPEN EMAIL (busy-my-web#524).
//
// The turns themselves are NOT kept here. Frame 40011642:370063 draws the app's ordinary chat —
// the question right-aligned in blue, the answer as prose with the #518 copy / re-ask pair under
// it — and that is components/Home/AiAnswer.vue over store/modules/timelineSearch.js. Asking the
// reader's question through a second conversation store would have meant a second rendering of the
// same thing, drifting the moment either was touched. So the ask goes to `timelineSearch/ask` with
// the scope lib/askScope.js already writes for an open thread ("thread:<timelineId>",
// busy-main-bff#20) and this module remembers only the three facts that chat does not know:
//
//   • WHICH email the conversation on screen belongs to, so the rail node, the briefing row and the
//     breadcrumb are drawn on that email and on no other;
//   • whether the conversation is OPEN in the middle (frames 4/6/7) or closed behind the mail
//     (frame 40011640:369482, where it has become a "💬 Conversation" row in the briefing header);
//   • what it is CALLED — bff#42's AI-written session title when there is one, else the name
//     lib/emailConversation.js derives from the sender.
//
// Opening a DIFFERENT email drops all three: a conversation belongs to the mail it was held about,
// and carrying it onto the next thread would put one email's answer under another's subject.

import { conversationTitle, conversationScope } from '@/lib/emailConversation'

// A FACTORY, not a shared object: every other module in this folder exports a literal, which is
// fine for the one store the app builds but makes two stores in one spec file share their state.
// Vuex has always accepted a function here, and a module this small has no reason to be the
// exception that cannot be built twice.
export const state = () => ({
  /** the timeline id of the email the conversation is about, or '' when there is none */
  threadId: '',
  /** is the conversation the thing in the middle right now? */
  open: false,
  /** its name — derived on open, replaced by a server/user title when one arrives */
  title: '',
})

export const getters = {
  threadId: s => s.threadId,
  /** Open, and about the email the caller is asking for (an id-less ask means "about anything"). */
  open: s => (timelineId) => !!s.open && (!timelineId || String(timelineId) === s.threadId),
  /** Is the conversation the thing in the middle at all? */
  active: s => !!s.open && !!s.threadId,
  title: s => s.title,
  /** Does THIS email have a conversation to reopen? (what draws the briefing header's row) */
  startedOn: (s, g, rootState, rootGetters) => (timelineId) => {
    if (!s.threadId || String(timelineId || '') !== s.threadId) return false
    return ((rootGetters?.['timelineSearch/conversation']) || []).some(t => t && String(t.question || '').trim())
  },
}

export const mutations = {
  SET_THREAD(s, id) { s.threadId = String(id || '') },
  SET_OPEN(s, open) { s.open = !!open },
  SET_TITLE(s, title) { s.title = String(title || '') },
}

export const actions = {
  /**
   * A question was asked from the reader's composer: the middle becomes the conversation.
   *
   * The chat is CLEARED first when this is a different email from the one the store remembers —
   * see the note at the top. Nothing is awaited before the view opens: the frames show the question
   * on screen with the answer filling in under it, which is what `timelineSearch/ask` already does.
   */
  async ask({ commit, state: s, dispatch }, { timelineId, question, senderName = '', title = '' } = {}) {
    const id = String(timelineId || '').trim()
    const text = String(question || '').trim()
    if (!id || !text) return false
    if (s.threadId !== id) {
      await dispatch('timelineSearch/clearConversation', null, { root: true })
      commit('SET_THREAD', id)
      // Named ONCE, on the question that opens it. A follow-up never re-derives: the name is the
      // conversation's, and rewriting it under the user mid-thread — because a later message
      // happened to carry a different display name — would rename something they are reading.
      // `conversationTitle` always answers with at least the plain word, so there is no "unnamed"
      // state for a later ask to fill in.
      commit('SET_TITLE', conversationTitle(senderName, title))
    }
    commit('SET_OPEN', true)
    // The chat's own name is what the top bar draws (components/Home/ChatTitle.vue over
    // `timelineSearch/title`), and frame 40011642:370063 heads the conversation "Khorasan Gostar
    // Company ⌄" — the same control, with the same chevron, so the picker the frame's chevron opens
    // is the one that already exists rather than a second one written for this journey.
    if (s.title) dispatch('timelineSearch/renameConversation', s.title, { root: true })
    await dispatch('timelineSearch/ask', { question: text, scope: conversationScope(id) }, { root: true })
    return true
  },

  /** The ✕ — the conversation closes and the email it was about comes back underneath it. */
  close({ commit }) {
    commit('SET_OPEN', false)
  },

  /** The briefing header's "💬 Conversation" row — what was said is put back in the middle. */
  reopen({ commit, state: s }, timelineId) {
    const id = String(timelineId || '').trim()
    if (!id || (s.threadId && s.threadId !== id)) return false
    commit('SET_OPEN', true)
    return true
  },

  /** A name for it — bff#42's session title, or a rename. An empty one is not a name. */
  rename({ commit }, title) {
    const name = String(title || '').trim()
    if (name) commit('SET_TITLE', name)
  },

  /** Another email was opened (or the reader was left): the conversation is not about it. */
  reset({ commit, dispatch, state: s }, { keepChat = false } = {}) {
    if (!keepChat && s.threadId) dispatch('timelineSearch/clearConversation', null, { root: true })
    commit('SET_THREAD', '')
    commit('SET_OPEN', false)
    commit('SET_TITLE', '')
  },
}

export default { namespaced: true, state, getters, actions, mutations }
